// external library
import React, { Suspense } from "react"
import { Spinner } from "react-bootstrap"
import { Navigate, Route, Routes } from "react-router-dom"
import { ToastContainer } from "react-toastify"

// internal library
import { AuthContextProvider, useAuthState } from "./library/AuthContext"

// styles
import "lightgallery.js/dist/css/lightgallery.css"
import "react-toastify/dist/ReactToastify.css"

// Conponents and Pages
const ProjectFlow = React.lazy(() => import("./components/ProjectFlow"))
const Assets = React.lazy(() => import("./pages/Assets"))
const Brand = React.lazy(() => import("./pages/Brand"))
const Project = React.lazy(() => import("./pages/Project"))
const Home = React.lazy(() => import("./pages/Home"))
const Login = React.lazy(() => import("./pages/Login"))
const NotFound = React.lazy(() => import("./pages/NotFound"))
const Export = React.lazy(() => import("./pages/Export"))
const Settings = React.lazy(() => import("./pages/Settings"))

interface AuthenticatedRouteProps {
  children: any
}

const AuthenticatedRoute = ({ children }: AuthenticatedRouteProps) => {
  const { isAuthenticated, loading } = useAuthState()

  if (loading) {
    console.info("AuthenticatedRoute Loading...")
    return (
      <div className="row w-100 justify-content-center mt-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (isAuthenticated) {
    return children
  }

  return <Navigate to="/login" />
}

const App = () => {
  const fallback = (
    <div className="h-100 w-100 fallback">
      <Spinner animation="grow" />
    </div>
  )
  return (
    <AuthContextProvider>
      <ToastContainer />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={fallback}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/project"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <Project />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/project/new"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <ProjectFlow />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/project/edit/:editProjectId"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <ProjectFlow />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/brand"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <Brand />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/assets"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <Assets />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <Settings />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/exportfb"
          element={
            <AuthenticatedRoute>
              <Suspense fallback={fallback}>
                <Export integration="facebook" />
              </Suspense>
            </AuthenticatedRoute>
          }
        />
        <Route
          path="/"
          element={
            <Suspense fallback={fallback}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={fallback}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </AuthContextProvider>
  )
}

export default App
